// ── Atlas GROUPED VIEW (agent-facing presentation over the flat map) ───────────────────────────────────
// A PURE, ADDITIVE layer: it reads the reconciled flat map (tokens/ids UNCHANGED — still the source of truth)
// and re-projects it as an entity-rooted tree the AI agents consume. Nothing here mutates the map.
//
// BUILD = FORWARD SKELETON + SEGMENT-SUFFIX MATCH (2026-07-26; docs/plans/atlas-variable-descriptions-and-
// grouping.md §12). The OLD parse-based build (invert every token via parseToken/splitField) is kept COMMENTED
// below — it was lossy on edge cases (overloaded `%`/`$`, un-invertible). The new build never inverts:
//   1. SKELETON — walk each imported entity's schema FORWARD (buildSkeleton, mirroring createEntityData's
//      unified AOB+ref recursion), emitting every leaf/handle as a SEGMENT ARRAY [entityId,…hops,field] with
//      its TypeData.description read INLINE. This is the tree spine + the match key set + the field meanings.
//   2. MATCH — reduce each flat-map token to a segment array (split on `% $$ $data$ $`, collapse a
//      local/global_variable%<var> root → its entity id) and match by SEGMENT-SUFFIX: the lane prefix, the
//      `section$<id>$` render-id, and the `%`-vs-`$` separator inconsistency all become leading noise the
//      suffix ignores. Each matched token → a VARIANT on that leaf (same field via newEntry/AOB_ref/section
//      folds into ONE field node; unification is inherent). Unmatched entity-field token → orphan (counted).
//   Validated on the real corpus (22 clients, 509 pages, 10,498 entity-field tokens): 99.1% match, 0 collisions.
//
// SHAPE:  canonical entity → schema-nesting → field → variants.  The section `@1` render-context is a
// variant attribute, never a tree level.  The model reads the shared field MEANING once, then emits a
// variant's id — never the token string.
//
// LANE ROUTING: `AOB_reference : Forge :: section : Spotlight`. A-S sees shared + section (not AOB_reference);
// A-F sees shared + AOB_reference (not section). Pass ctx.agent to pre-filter. `parseToken`/`resolveCanonical`
// stay EXPORTED (attachDescriptions still uses them for single-token description resolution).

import { VariableUsage, formatUsageSummary } from './atlas-usage';
import { usageOnMaps } from './usage-on-maps';
import { Page } from '../../../../model/page.model';

// ── inputs ──
/** One flat-map entry adapted for grouping. `usage` is what uses the entry on the page as it stands (a global or
 *  navigation entry: this page's uses only), computed now — never the `usages` saved on the entry. */
export interface GroupInput {
  token: string;   // the map key (unchanged identity)
  id: string;      // the minted display handle ($A6, $12, …) — what the model outputs
  type: string;
  scope: string;   // local | global | newEntry | AOB_reference | AAOB | entityName | NAVIGATE | section
  usage?: VariableUsage | null;
}

export interface GroupCtx {
  getEntityById: (id: string) => any;          // resolve an entity id → { id, name, data, description }
  localVars: Record<string, any>;              // page.localVariable (var → value; entity vars carry .entityId)
  globalVars: Record<string, any>;             // the global store (var → value)
  agent?: 'spotlight' | 'forge';               // lane routing; omit to include every lane
  pageEntites?: string[];                      // page.entites — the AUTHORITATIVE skeleton roots (import gate).
  /** page id → Page.description, for NAVIGATE targets. Keyed by ID because that is what the token is. */
  pageDescriptions?: Record<string, string>;
  varDescriptions?: Record<string, string>;    // var name → meaning. Merge page.localVariableDescriptions
                                               // with the global store's atlasMeta.varDescriptions.
                                               // Omit → roots are derived from the entries (scratchpad/no-page use).
}

// ── outputs ──
export interface GroupedVariant {
  id: string;                 // $A6 — the handle the model emits
  // The raw flat-map token this id was minted for (`local_variable%tempLead$fullName`). Added 2026-07-27:
  // without it an id cannot be resolved back to a real path, so a consumer had to keep the flat map alongside
  // the grouped one. Carrying it makes the grouped view self-sufficient — which is the point, since it is
  // assembled on demand and is the only structure the agent is given.
  token?: string;
  type: string;
  lane: string;               // readable lane label (see LANE_LABEL)
  scope: string;              // raw scope (routing / debugging)
  via?: string;               // source var when the root was a list-/entity-var (e.g. "pegagusFileLIst")
  sectionCtx?: string;        // section render-context (@-instance) — opaque slot marker
  // ── USAGE — who already touches this variable (2026-07-27) ──────────────────────────────────────
  // THE selection signal for Atlas. A variable three existing functions write is a strong candidate; one
  // nothing has ever touched is weak. Given STRUCTURED, not as prose: Atlas has to reason over it (count
  // the writers, check the access), and `description` was a pre-joined string it would have to parse back.
  // Absent entirely when the crawler found nothing — absence IS the signal, so an empty object would lie.
  // All three lanes are carried in FULL, not as counts (2026-07-27). A count says "2 components use this";
  // Atlas needs to know it is the VALUE of a lead-status dropdown and the SOURCE of a table column — that is
  // what makes a variable the right pick, and the binding's own description is the clearest statement of
  // intent anywhere in the system ("why this component is wired to this variable").
  usage?: {
    // page functions touching it — access tells read from write, and `description` says WHAT that function
    // does. The name frequently carries nothing (`system_fn_flagChange_5ed8`, `defaultFunction`), so the
    // description is the part that turns "three functions write this" into evidence Atlas can weigh.
    functions: { name: string; access: 'read' | 'write' | 'both'; ops?: string[]; description?: string }[];
    // SPOTLIGHT lane: which component, which property, and which way the data flows
    components: { id: string; componentName: string; property: string; direction: 'input' | 'output'; description?: string }[];
    // SPOTLIGHT lane: table/repeater bindings — `multiData` means this variable drives the rows
    sections: { id: string; name: string; binding: 'hidden' | 'multiData' | 'multiDataIndex' }[];
  };
  // Human-readable form of `usage` ("ƒ3(defaultFunction:w, …)") — convenience for rendering only; every
  // fact in it is in `usage`. Kept so existing readers do not break.
  description?: string;
  // ── standalone page/global vars only (2026-07-27) ──
  // An entity FIELD gets its identity from GroupedField.field + the entity it hangs off. A standalone
  // scalar/list had neither, so it rendered as "$D1 — Boolean · local var" with nothing saying it is
  // `isLoading`. An agent cannot use that. Both are read-only presentation; the id stays the handle.
  name?: string;              // the variable's own name  (isLoading, BudgetEnity)
  // WHY THIS HANDLE EXISTS — the backfilled description of the VARIABLE this variant is rooted in
  // (page.localVariableDescriptions / atlasMeta.varDescriptions). Two different things are described at
  // two different levels, and conflating them loses one (2026-07-28):
  //   GroupedField.description  = what the DATA means   — "list of transactions in this budget period"
  //   GroupedVariant.meaning    = why this HOLDER exists — "the budget currently open in the editor"
  // The field meaning is shared by every variant by construction, so it can never tell two apart; the
  // holder's scenario can. Populated for standalone vars (name → varDescriptions) AND for entity-field
  // variants whose root is a real variable (via → varDescriptions).
  // ABSENT on synthesised lanes — newEntry / AOB_reference / entityName / section have no user-created
  // variable behind them, so there is nothing to describe per-variable. Their scenario IS the lane, which
  // is constant per lane and therefore stated once in the SYSTEM PROMPT rather than copied onto every row.
  meaning?: string;
}
export interface GroupedField {
  field: string;              // raw field name (token identity) — stated once, shared across variants
  description?: string;       // the shared field MEANING — TypeData.description, read inline from the skeleton (§9)
  /**
   * ENTITY_REFERENCE fields only: the entity id this reference POINTS AT (2026-07-28).
   *
   * A reference has NO variants — nothing addresses the link itself, only fields reached through it — so
   * until now it was invisible as anything but a route. But it is filterable IN ITS OWN RIGHT, and that is
   * how you fetch children of a parent:
   *     {"field":"leadRefference","op":"_eq","value":{"var":"globalVariable$editOotbLeadsEntity"}}
   * The compared value is an ENTITY VARIABLE, so knowing WHICH entity it must hold is the whole point —
   * `type: 'ENTITY_REFERENCE'` alone cannot express it. 5 of the 33 query-field sites in the corpus are
   * this pattern; without this they are inexpressible.
   */
  refEntity?: string;
  /**
   * THE QUERY-COLUMN HANDLE (2026-07-29) — `$Q1_7`, or `$Q1_7.$Q2_7` for a column reached through a
   * reference. Distinct from `variants[].id`, and the distinction is load-bearing:
   *
   *   variants[].id   `$B3`     one ROUTE to READ this field's value  → "var" / "target" slots
   *   columnId        `$Q1_7`   the FIELD AS A COLUMN, to filter on   → "field" slot
   *
   * One field has N variants (N holders route to it) but is exactly ONE column, so a variant id could
   * never serve — and the two resolve to different strings: `$B3` → the full traversal token
   * `local_variable%tempLead$fullName`, `$Q1_7` → the bare `fullName`.
   *
   * Composed in `buildSkeleton` from ids stamped on each entity's own TypeData; never stored. ABSENT
   * when any link is unstamped (an entity not yet re-saved, or a client P2 did not cover) and ABSENT on
   * AAOB subtrees, which are stamped for identity but never offered as columns — EXCEPT the AAOB
   * CONTAINER itself, which is a real filterable column and keeps its handle (2026-08-04, see `aaob`).
   */
  columnId?: string;
  /**
   * THIS COLUMN IS AN AAOB (2026-08-04). Filtering or sorting it needs `aaobKey` (which runtime label)
   * and `aaobType` (which of the four value columns) alongside `field` — a plain `field`+`op` is not a
   * complete condition here, which is why the flag has to reach Forge rather than being inferred.
   */
  aaob?: boolean;
  /**
   * The scalar type of a column that has NO variants (2026-08-05).
   *
   * A field can be FILTERABLE without being READABLE. `variants[].id` is a route to read a value; a column
   * inside an AOB that hangs off a REFERENCE has no such route — nothing mints a Blockly variable for
   * `ootbLeads.metaFormRef.questions.key` — yet it is a perfectly legal column to filter on, and the human
   * picker offers it. Those nodes reach the tree from the SKELETON rather than from a matched token, so
   * they carry no variant to read a type from, and `collectColumns` needs one to emit them.
   */
  columnType?: string;
  /**
   * THE REFERENCE-LINK HANDLE (2026-07-30) — ENTITY_REFERENCE fields only. What Forge emits in
   * `entityReferences[].field` on CREATE_ENTITY / UPDATE_ENTITY to LINK this reference at a record.
   *
   * A third job for one field, and the three are not interchangeable:
   *
   *   variants[].id   $B3      READ its value       -> "var" / "target"
   *   columnId        $Q28_7   FILTER on it         -> "conditions[].field"
   *   linkId          $A6      LINK it on a write   -> "entityReferences[].field"
   *
   * It resolves to a Blockly `ENTITYREFERENCEFORCRUD` variable
   * (`<entityId>$<field>%<refEntity>_for_CRUD`), which is a DIFFERENT namespace from a column name — so
   * `columnId` cannot serve here and vice versa. Sourced from the map's `refLink` lane; absent until a
   * page has been reconciled since that lane landed.
   */
  linkId?: string;
  /**
   * The raw flat-map token `linkId` resolves to —
   * `tasks_NzMxOT$leadRefference%ootbLeads_for_CRUD`.
   *
   * Carried for the same reason `GroupedVariant.token` is: without it an id cannot be turned back into a
   * real Blockly variable, so a consumer would have to keep the flat map alongside the grouped one. It is
   * NOT the column name — `field` is `leadRefference`, this is an ENTITYREFERENCEFORCRUD variable in a
   * different namespace, and only this form lets `guardEntityReferences` parse the target entity out of
   * the `%<refEntity>_for_CRUD` tail.
   */
  linkToken?: string;
  /** What already writes the link (a save attaching the relationship): the same selection signal a variant's
   *  `usage` is, for the link handle. Absent when nothing uses it. */
  linkUsage?: GroupedVariant['usage'];
  // Handles for this field. EMPTY for a reference hop: `metaFormRef` is a real ENTITY_REFERENCE field of
  // ootbLeads, but nothing addresses it — 0 tokens end at it while 140 pass through it
  // (`ootbLeads%metaFormRef$metaFormId`). It is a place you go through, not a value you read or write.
  variants: GroupedVariant[];
  // ── nesting lives HERE (2026-07-27), not in a parallel `children` on the entity ──
  // A field whose type is ARRAY_OF_OBJECT or ENTITY_REFERENCE has an inner shape, and that shape belongs
  // under the field that owns it. Previously the entity carried a separate `children[]` and the renderer
  // re-attached each child to its field BY NAME. One recursive concept replaces two parallel ones, and the
  // parentage is structural rather than reconstructed.
  fields?: GroupedField[];
}
/**
 * A variable that HOLDS this entity — a list of its records, or one record (2026-07-27).
 *
 * These are not fields, so they were never variants of a GroupedField; they used to sit flat in
 * localVars/globalVars with only `via` on the fields hinting they existed. But they must be addressable
 * in their own right: `SET_ENTITY_FROM_LIST` takes a LIST and an ENTITY as its two operands, and `QUERY`
 * writes into a list. Hanging them off the entity says plainly "these variables carry Budget records",
 * which is also exactly what a field's `via` refers back to.
 */
export interface EntityHolder {
  id: string;                 // the handle the model emits
  token?: string;             // the flat-map key it resolves to
  name: string;               // the variable's own name (ootbLeadsList, tempOotbLeadsEntity)
  kind: 'list' | 'entity';    // a collection of records, or a single record
  // The platform type, verbatim from the map entry (2026-07-28). `kind` is a two-way summary and cannot be
  // widened back into a real type — a hydrator reading only `kind` has to GUESS ARRAY_OF_OBJECT vs an entity
  // variable, and a guessed type decides which ACTIONS Forge believes are legal. Carry the fact instead.
  type: string;
  scope: string;              // local | global
  meaning?: string;           // backfilled description of the variable
  usage?: GroupedVariant['usage'];   // who already reads/writes it — same selection signal as a field's
}

export interface GroupedEntity {
  // `key` REMOVED 2026-07-27 — it was the entity id, which is exactly `token`. Once the node carries its
  // own handle there is nothing left for a second copy to do. (Nested levels never had a distinct key
  // either: key === name on all 104 nested nodes measured.)
  id?: string;                // the entityName token's minted handle — what CREATE_ENTITY / QUERY emit
  token?: string;             // the entity id (the flat-map key). Root nodes only.
  name: string;               // display name — entity.name for a root ("Budget", NOT "Budget_OTI1Nj"),
                              // the raw segment for a nested level. Not derivable from the id: the
                              // `_OTI1Nj` suffix cannot be told apart from a real name ending.
  description?: string;       // Entity.description (root nodes only)
  /**
   * `Entity.status` — 'SYNCED' | 'READY' | 'LOCAL' | 'NEW' (2026-08-01).
   *
   * Carried for ONE check, which is worth the field: a LOCAL entity was created in the editor and never
   * synced, so it has NO DATABASE TABLE. Querying, aggregating or creating one fails at runtime with
   * "quering the LOCAL Entity which is not possible" — and nothing else in the payload distinguishes it
   * from a real table. Without this the validator's central chokepoint (checkEntity) silently stops
   * firing the moment validation moves to AtlasContext.
   */
  status?: string;
  heldBy?: EntityHolder[];    // variables carrying records of this entity (root nodes only)
  fields: GroupedField[];     // nesting is inside a field's own `fields` — see GroupedField
  // `children: GroupedEntity[]` REMOVED 2026-07-27 — every child DID correspond to a real schema field
  // (ARRAY_OF_OBJECT or ENTITY_REFERENCE); keeping them in a sibling array meant the tree said "Budget has
  // a child called transaction" while `transaction` was also sitting in `fields`, and every consumer had to
  // rejoin them by name. Now the inner shape hangs off the field itself.
}
// A standalone entry WITH its minted id. `standalone[].names` gives the human list; an agent that must
// EMIT a handle needs the id too (a NAVIGATE target is `$7`, not the page name).
// `name` REMOVED 2026-07-27 — it equalled `token` on 660 of 660 items measured. Read `token`.
export interface StandaloneItem { id: string; token?: string; type: string; scope: string; usage?: GroupedVariant['usage']; description?: string; }

export interface GroupedView {
  entities: GroupedEntity[];                        // entity-rooted groups
  localVars: GroupedVariant[];                      // standalone local page vars (scalars + lists)
  globalVars: GroupedVariant[];                     // standalone global vars
  standalone: { group: string; names: string[]; items: StandaloneItem[] }[]; // entity names, AAOB fields, navigation targets
  // orphans = entity-field tokens that matched no skeleton leaf (stale).
  // droppedUnimported / unimportedVars = global entity vars whose entity this page never imported (§1b).
  // They are NOT orphans and NOT a builder failure: the import gate says the variable is unavailable here.
  // Reported rather than dropped silently so "no such variable on this page" is distinguishable from a bug.
  // droppedLane / droppedLanes = tokens withheld by agent lane routing (HIDDEN_FOR). Also reported: a lane the
  // agent cannot see is a capability it cannot use, and that must be visible rather than inferred from a gap.
  stats: { entries: number; parsed: number; unified: number; orphans: number; droppedUnimported?: number; unimportedVars?: string[]; droppedLane?: number; droppedLanes?: string[];
    // list/entity vars whose entity is not a skeleton root here — kept flat in localVars rather than lost.
    heldByUnplaced?: number };
}

// ── lane labels (readable) ──
const LANE_LABEL: Record<string, string> = {
  newEntry: 'new-record draft',
  'local·entity': 'local entity field',
  'global·entity': 'global entity field',
  AOB_reference: 'AOB reference',
  section: 'table cell',
};
// scopes hidden from each agent (§11 #6)
// Lane routing per agent — the original A-S / A-F split stands (CONFIRMED 2026-07-27):
//   spotlight → sees `section`,  hides `AOB_reference`
//   forge     → sees `AOB_reference`, hides `section`
// The hide itself is deliberate. What was wrong was that it dropped tokens with NO trace: a lane the agent
// cannot see is a capability it cannot use, and nothing said so. The drop is now counted
// (stats.droppedLane / droppedLanes) — hidden, but never silent.
const HIDDEN_FOR: Record<string, Set<string>> = {
  spotlight: new Set(['AOB_reference']),
  forge: new Set(['section']),
};

// ── PARSER: token → semantic parts (all 8 scopes; validated 100% on the real corpus) ──
export interface Parsed {
  kind: 'entityField' | 'pageVar' | 'standalone' | 'unparsed';
  // entityField
  root?: string; nesting?: string[]; field?: string | null; lane?: string; sectionCtx?: string;
  // pageVar
  space?: 'local' | 'global'; name?: string; isList?: boolean;
  // standalone
  group?: string;
  token?: string;
}

// split "<rootAndPath>[$$|$data$|$]<field>" honoring the three field-join conventions
function splitField(s: string): { rootAndPath: string; field: string | null } {
  if (s.includes('$data$')) { const i = s.indexOf('$data$'); return { rootAndPath: s.slice(0, i), field: s.slice(i + 6) }; }
  const dd = s.lastIndexOf('$$'); if (dd >= 0) return { rootAndPath: s.slice(0, dd), field: s.slice(dd + 2) };
  const d = s.lastIndexOf('$'); if (d >= 0) return { rootAndPath: s.slice(0, d), field: s.slice(d + 1) };
  return { rootAndPath: s, field: null };
}
function splitPath(p: string): { root: string; nesting: string[] } { const segs = p.split('%'); return { root: segs[0], nesting: segs.slice(1) }; }

function parseSection(token: string): Parsed {
  const s = token.slice('section$'.length);
  const sid = s.slice(0, s.indexOf('$'));
  const rest = s.slice(s.indexOf('$') + 1);
  if (rest.startsWith('section$')) {                       // double-section (table-in-table): unwrap once, best effort
    const inner = parseSection(rest);
    if (inner.kind === 'entityField') return { ...inner, lane: 'section', sectionCtx: sid + '›' + (inner.sectionCtx || '') };
  }
  const { rootAndPath, field } = splitField(rest);         // <mdEntity>$<listPath>
  const fx = rootAndPath.indexOf('$');
  const mdEntity = fx >= 0 ? rootAndPath.slice(0, fx) : rootAndPath;
  const list = splitPath(fx >= 0 ? rootAndPath.slice(fx + 1) : '');
  let root: string, nesting: string[];
  if (mdEntity === 'local_variable' || mdEntity === 'global_variable') { root = list.root; nesting = list.nesting; }
  else if (mdEntity.startsWith('local_variable%') || mdEntity.startsWith('global_variable%')) { root = mdEntity.split('%')[1]; nesting = [list.root, ...list.nesting]; }
  else { root = mdEntity; nesting = [list.root, ...list.nesting]; }
  return { kind: 'entityField', root, nesting: nesting.filter(Boolean), field, lane: 'section', sectionCtx: sid };
}

export function parseToken(token: string, scope: string): Parsed {
  if (scope === 'NAVIGATE') return { kind: 'standalone', group: 'Navigation targets', name: token };
  if (scope === 'entityName') return { kind: 'standalone', group: 'Entity names', name: token };
  if (scope === 'AAOB') return { kind: 'standalone', group: 'Extended-attribute (AAOB) fields', name: token };
  if (token.startsWith('localVariable$')) { let n = token.slice(14); const isList = n.endsWith('.data'); if (isList) n = n.slice(0, -5); return { kind: 'pageVar', space: 'local', name: n, isList }; }
  if (token.startsWith('globalVariable$')) { let n = token.slice(15); const isList = n.endsWith('.data'); if (isList) n = n.slice(0, -5); return { kind: 'pageVar', space: 'global', name: n, isList }; }
  if (token.startsWith('section$')) return parseSection(token);
  if (token.startsWith('entity_reference_for_AOB$')) { const { rootAndPath, field } = splitField(token.slice('entity_reference_for_AOB$'.length)); const { root, nesting } = splitPath(rootAndPath); return { kind: 'entityField', root, nesting, field, lane: 'AOB_reference' }; }
  if (token.startsWith('local_variable%') || token.startsWith('global_variable%')) { const space = token[0] === 'l' ? 'local' : 'global'; const { rootAndPath, field } = splitField(token.slice(token.indexOf('%') + 1)); const { root, nesting } = splitPath(rootAndPath); return { kind: 'entityField', root, nesting, field, lane: space + '·entity' }; }
  const { rootAndPath, field } = splitField(token); const { root, nesting } = splitPath(rootAndPath);  // newEntry / entity-id projection
  if (!root) return { kind: 'unparsed', token };
  return { kind: 'entityField', root, nesting, field, lane: scope };
}

// ── canonical entity resolve: entity id → itself; list-var / entity-var → its .entityId ──
export function resolveCanonical(root: string, ctx: GroupCtx): { id: string; name: string } {
  const direct = ctx.getEntityById(root);
  if (direct && direct.id) return { id: direct.id, name: direct.name || direct.id };
  const lv = ctx.localVars[root]; const gv = ctx.globalVars[root];
  const via = (lv && (lv.entityId || lv.entityName)) || (gv && (gv.entityId || gv.entityName));
  if (via) { const e = ctx.getEntityById(via); return { id: via, name: (e && e.name) || via }; }
  return { id: root, name: root };
}

// ── describe a variant: USAGE ONLY (2026-07-27) ───────────────────────────────────────────────────
// It used to return `<type> · <lane>[ · via …][ · @ctx][ · usage]` — a prose re-serialisation of four
// fields that sit RIGHT NEXT TO IT on the same object, already structured. Every consumer then had to
// parse the string back apart to avoid printing "Number" twice. Only the usage summary was unique.
// Now it returns just that, and is omitted when there is no usage. `type`/`lane`/`via`/`sectionCtx`
// stay where they belong — as data on the variant, composed by whoever renders.
//   before:  "Number · global entity field · via BudgetEnity · ƒ1(addTransType:w)"
//   after:   "ƒ1(addTransType:w)"        (absent entirely when the crawler found no usage)
function describe(_v: { type: string; lane: string; via?: string; sectionCtx?: string }, usage?: VariableUsage | null): string {
  return usage ? formatUsageSummary(usage) : '';
}

/** VariableUsage → the compact structured form carried on a variant. `undefined` when nothing touches it. */
function usageOf(u?: VariableUsage | null): GroupedVariant['usage'] {
  if (!u) return undefined;
  // `description` is carried (2026-08-09). It was dropped here — and ONLY here — which severed the lane
  // one hop before the renderer: `atlas-usage.ts` put the function's description on the usage record,
  // `atlas-picklist.util.ts` renders it as `save:w "what that function does"`, and this rebuild in between
  // listed the three fields it wanted and lost the fourth. Measured after fixing: 0 → 288 descriptions
  // across the 17 corpus maps, on 80 of 330 functions.
  //
  // It matters more than the other lanes because half the estate's function NAMES carry nothing —
  // `defaultFunction`, `system_fn_flagChange_5ed8` — so "three functions write this" is a far weaker
  // signal than "the function that saves the transaction writes this", and the latter is what Atlas is
  // asked to weigh.
  const functions = (u.functions || []).map((f) => ({
    name: f.name,
    access: f.access,
    ...(f.ops?.length ? { ops: f.ops } : {}),
    ...(f.description ? { description: f.description } : {}),
  }));
  const components = (u.components || []).map((c) => ({ id: c.id, componentName: c.componentName, property: c.property, direction: c.direction, description: c.description }));
  const sections = (u.sections || []).map((x) => ({ id: x.id, name: x.name, binding: x.binding }));
  if (!functions.length && !components.length && !sections.length) return undefined;   // absence is the signal
  return { functions, components, sections };
}

// ── build the tree ──
// `key` stays INTERNAL (map keying, skeleton lookup); the frozen output publishes it as `token` instead.
interface MutField { field: string; description?: string; refEntity?: string; columnId?: string; aaob?: boolean; columnType?: string; linkId?: string; linkToken?: string; linkUsage?: GroupedVariant['usage']; variants: GroupedVariant[]; fields: Map<string, MutField>; }
interface MutEntity { key: string; name: string; desc?: string; status?: string; id?: string; root?: boolean; heldBy?: EntityHolder[]; fields: Map<string, MutField>; }
/** get-or-create a field on an entity or inside another field — the single nesting primitive */
function ensureField(holder: { fields: Map<string, MutField> }, name: string, desc?: string, refEntity?: string, columnId?: string): MutField {
  let f = holder.fields.get(name);
  if (!f) { f = { field: name, description: desc || undefined, refEntity: refEntity || undefined, columnId: columnId || undefined, variants: [], fields: new Map() }; holder.fields.set(name, f); }
  else {
    if (desc && !f.description) f.description = desc;
    if (refEntity && !f.refEntity) f.refEntity = refEntity;   // the hop learns its target on any pass
    if (columnId && !f.columnId) f.columnId = columnId;       // …and its column handle, same way
  }
  return f;
}
function freezeField(f: MutField): GroupedField {
  const out: GroupedField = { field: f.field, description: f.description, variants: f.variants };
  if (f.refEntity) out.refEntity = f.refEntity;
  if (f.columnId) out.columnId = f.columnId;
  if (f.aaob) out.aaob = true;
  if (f.columnType && !(f.variants || []).length) out.columnType = f.columnType;
  if (f.linkId) out.linkId = f.linkId;
  if (f.linkToken) out.linkToken = f.linkToken;
  if (f.linkUsage) out.linkUsage = f.linkUsage;
  if (f.fields.size) out.fields = [...f.fields.values()].map(freezeField).sort((a, b) => a.field.localeCompare(b.field));
  return out;
}
function freeze(n: MutEntity): GroupedEntity {
  const out: GroupedEntity = {
    name: n.name, description: n.desc,
    fields: [...n.fields.values()].map(freezeField).sort((a, b) => a.field.localeCompare(b.field)),
  };
  // Root nodes are addressable: `token` is the entity id, `id` the handle CREATE_ENTITY / QUERY emit.
  // Nested levels are shape, not a thing you can name, so they carry neither.
  if (n.root) { out.token = n.key; if (n.id) out.id = n.id; if (n.status) out.status = n.status; }
  if (n.heldBy?.length) out.heldBy = n.heldBy.sort((a, b) => a.name.localeCompare(b.name));
  return out;
}

// ── FORWARD SKELETON + SEGMENT-SUFFIX helpers (see header + §12) ──
/**
 * Never enters the grouped view. THREE UNLIKE THINGS share this set — do not treat it as one rule
 * (measured 2026-07-29 across every client):
 *
 *   id                 the row's UUID primary key
 *   id_auto_increment  a SCALAR with real query value — the corpus's canonical "newest first" sort,
 *                      7 of 33 query-field sites. Hidden here only because it is hidden as a VARIABLE;
 *                      identity is no longer the blocker (it carries a permanent `atlasColumnId`), so
 *                      letting it through FOR THE COLUMN PURPOSE is an open decision.
 *   parent_id          AOB PLUMBING, not a reference anyone models. 273 found, **0 at entity level,
 *                      all 273 inside an AOB, all typed ENTITY_REFERENCE** — it is the child table's FK
 *                      to its parent row, so FOLLOWING IT IS BY DEFINITION A LOOP back where you came
 *                      from. Permanently excluded, structurally. It must NOT ride along if
 *                      `id_auto_increment` is ever let through.
 *
 * Consequence worth knowing: every reference that loops back to a walk's ROOT does so through
 * `parent_id` — which is why not seeding the root into `seen` (below) is correct AND a no-op.
 */
const SKELETON_SKIP = new Set(['id', 'id_auto_increment', 'parent_id']);
/** TypeData.type → the scalar name used everywhere else in the view (and in hydrate's SCALAR_TYPES). */
const SKEL_SCALAR_TYPE: Record<string, string> = {
  STRING: 'String', NUMBER: 'Number', BOOLEAN: 'Boolean', TIMESTAMP: 'TimeStamp',
  GEOMETRY: 'GEOMETRY',
};
interface SkelLeaf {
  segs: string[]; type: string; desc: string; kind: 'scalar' | 'aob-handle' | 'ref-handle';
  /**
   * THE AAOB CONTAINER ITSELF, as opposed to anything inside it (2026-08-04).
   *
   * `inAaob` conflated the two, and the distinction is the whole of G2. `customFieldData` IS a filterable
   * column — `add_condition` / `add_order` filter an AAOB through the SAME `field` slot as any other
   * column, plus `aaobKey` + `aaobType`. Its interior (`systemKey`, `systemString`, …) is storage and
   * must stay unofferable. Withholding both left the two blocks that lane exists to serve with no id to
   * name, so an AAOB filter had no valid spelling at all.
   */
  aaobRoot?: boolean;
  /**
   * ref-handles only: the entity this reference POINTS AT. Kept because a reference is filterable in its
   * own right — `WHERE leadRefference _eq <a lead>` — and to write that you must know which entity the
   * compared variable has to hold. 5 of the 33 query-field sites in the corpus are exactly this
   * (fetch-the-children-of-this-parent); without it they cannot be expressed at all.
   */
  refEntity?: string;
  /**
   * THE QUERY-COLUMN HANDLE — `$Q1_7` for an own column, `$Q1_7.$Q2_7` for one reached through a
   * reference (2026-07-29). What Forge emits in `conditions[].field` / `orderBy.field`;
   * `resolveTokens` swaps in the real dotted column name.
   *
   * COMPOSED HERE, NEVER STORED. Each entity stamps only its own fields (`TypeData.atlasColumnId`,
   * P1/P2), so no entity holds a closure and reference cycles are impossible at rest. This walk is the
   * one place that has both ends of the chain — it already descends references via `getEntityById`, and
   * the TypeData it descends into carry the TARGET entity's ids — so the chain falls out of a second
   * accumulator running beside `segs`. No lookups, no registry.
   *
   * ABSENT when any link in the chain is unstamped. An entity that has never been saved since P1, or a
   * client the P2 backfill did not cover (prod `MjU0OD`), yields no handle rather than a broken one like
   * `undefined.$Q2_3` — which would resolve to nothing and read as a real column.
   */
  columnId?: string;
  /**
   * Inside an AAOB subtree (`extendedAttributes === true`), including the AAOB handle itself.
   *
   * These are STAMPED but must never be OFFERED as query columns: an AAOB is filtered through its own
   * `aaobField` + `key` mechanism, not as a plain column, and its labels are runtime rows rather than
   * schema. The pickers already exclude `extendedAttributes === true` for the same reason. Identity is
   * not offering — see entity-column-ids.util.ts.
   */
  inAaob?: boolean;
}

// Walk an entity's schema FORWARD (createEntityData's unified recursion — descends BOTH AOB and ref, cyclic-safe),
// emitting every leaf/handle as [entityId, …hops, field] with its TypeData.description inline.
function buildSkeleton(entityId: string, getEntityById: (id: string) => any): SkelLeaf[] {
  const out: SkelLeaf[] = [];
  // `pathIds` runs BESIDE `pathSegs`: the same walk, one accumulator holding names and the other holding
  // `atlasColumnId`s. A null anywhere in it poisons the chain — see `composeId`.
  const composeId = (pathIds: (string | undefined)[], own: string | undefined): string | undefined => {
    // ATOMIC, NOT COMPOSABLE (2026-08-12). This used to join with a dot — `$Q9_8.$Q24_8.$Q10_7` — and the
    // dot was doing no work: `resolveTokens` uses the whole string as a literal lookup key and never splits
    // it. What it DID do was invite the agent to take a piece, and every piece is itself a legal column
    // with a different meaning, so a partial pick is a different filter rather than an error nobody can
    // reject. Measured once on benchmark V08: it took the tail `$Q19_9` instead of `$Q10_7.$Q19_9`.
    //
    // Dropping the inner `$` matters as much as dropping the dot. The `$` is what marks a substring as a
    // handle, so `$Q9_8_$Q24_8` would still display two liftable ids — and every regex in the estate that
    // scans for `$`-prefixed handles would match a composed id several times over.
    //
    // AND IT RESTORES VALIDATION. `checkSingleCondition` skips any condition whose `field` contains a dot,
    // because a dotted NAME path cannot be resolved against the flat PageContext. Validation runs in id
    // space, so a dotted ID hit that same branch: all 1,421 composed columns in the recorded corpus were
    // skipped. Without the dot, that check applies to name paths only, as intended.
    const chain = [...pathIds, own];
    // The predicate is a type guard, not just `!!x`, so `every`'s `this is S[]` overload narrows `chain`
    // to `string[]` for the `slice` below — a plain truthiness callback leaves it `(string | undefined)[]`.
    if (!chain.every((x): x is string => !!x)) return undefined;     // all-or-nothing, never a partial path
    return chain.length === 1 ? chain[0] : '$Q' + chain.map((s) => s.slice(2)).join('_');
  };
  const walk = (data: any[], pathSegs: string[], pathIds: (string | undefined)[], seen: Set<string>, inAaob: boolean) => {
    if (!Array.isArray(data)) return;
    for (const f of data) {
      if (!f || SKELETON_SKIP.has(f.name)) continue;
      const segs = [...pathSegs, f.name];
      const ids = [...pathIds, f.atlasColumnId];
      const columnId = composeId(pathIds, f.atlasColumnId);
      const desc = (f.description && String(f.description).trim()) || '';
      if (f.type === 'ARRAY_OF_OBJECT') {
        // An AAOB taints its whole subtree: `extendedAttributes` sits on the container, and the children
        // below it are just as unofferable as the container itself.
        const aaob = inAaob || f.extendedAttributes === true;
        // …but the CONTAINER is still a column. Only true when this field is itself the AAOB and is not
        // already sitting inside one — an AAOB nested in an AAOB is interior, not a filterable column.
        const aaobRoot = !inAaob && f.extendedAttributes === true;
        out.push({ segs, type: f.type, desc, kind: 'aob-handle', columnId, inAaob: aaob || undefined, aaobRoot: aaobRoot || undefined });
        walk(f.data, segs, ids, seen, aaob);   // `seen` UNCHANGED — an AOB is not a hop, it is own storage
      }
      else if (f.type === 'ENTITY_REFERENCE') {
        // ALWAYS emit the reference itself (2026-07-27), then walk through it. It used to be emitted only
        // when unresolvable, so a followed reference vanished from the tree and its inner fields had no
        // parent to hang under — which is why nesting needed a parallel `children[]`. `metaFormRef` IS a
        // field of ootbLeads; it simply has no token of its own.
        out.push({ segs, type: f.type, desc, kind: 'ref-handle', refEntity: f.referenceEntityName, columnId, inAaob: inAaob || undefined });
        const ref = getEntityById(f.referenceEntityName);
        // Descending yields the TARGET entity's TypeData, whose `atlasColumnId`s belong to that entity —
        // which is exactly what makes `$Q1_7.$Q2_7` come out right with no lookup.
        if (ref && Array.isArray(ref.data) && !seen.has(f.referenceEntityName)) walk(ref.data, segs, ids, new Set([...seen, f.referenceEntityName]), inAaob);
      } else out.push({ segs, type: f.type, desc, kind: 'scalar', columnId, inAaob: inAaob || undefined });
    }
  };
  const ent = getEntityById(entityId);
  // THE ROOT IS NOT SEEDED (2026-07-29). `seen` blocks revisiting an ANCESTOR, and the root is not an
  // ancestor of itself — so `orders → vendorRef → Aboutorders → order → orders` comes back ONCE, then
  // stops. This aligns the skeleton with `PageService.createEntityData`, the other implementation of this
  // same recursion, whose comment states it outright: *"the top parent can be repeated once as the
  // parents is not included and thats intended."*
  //
  // It also removes a real non-determinism. Seeding put ONE spelling into `seen` while a reference
  // carries `referenceEntityName`, which may be the doc id (`orders_MjE1OD`) where the caller passed the
  // name (`orders`) — so the guard fired or not depending on which key the caller happened to use. Same
  // entity, 102 leaves or 240. `rootIds` comes from `page.entites` (doc ids), so the guard WAS firing.
  //
  // MEASURED, all clients, 390 entities: only 6 chains genuinely return to their root, all in Njc3OD
  // (orders/user/vendor, a real A→B→C→A through AOB-held references). Effect there:
  //     orders_MjE1OD  102 → 240 leaves     vendor_ODA3OT  102 → 143     user_MTAyMj  102 → 127
  // The 18-page benchmark corpus is unaffected (1472 field nodes before and after) — Njc3OD is not in it.
  //
  // Count the TYPE, not the name: 33 of 510 fields carrying a `referenceEntityName` are STRING/NUMBER
  // with a stale value (`eWayBill.itemList.productId` is a NUMBER pointing at `eWayBill`). Scanning by
  // `referenceEntityName` truthiness reports 46 loops that do not exist; this walk branches on `f.type`,
  // which is why they never mattered.
  // `pathIds` starts EMPTY, not with the entity's own `$E_n`: a column id is `$Q<e>_<n>` and already
  // names its entity, so prefixing the entity handle would double it.
  if (ent && Array.isArray(ent.data)) walk(ent.data, [entityId], [], new Set(), false);
  return out;
}

// Reduce a flat-map token to its segment array; collapse a local/global_variable%<var> root → the var's entity id
// (the ONLY semantic step — `.entityId`, not string parsing). All lane prefixes / render-ids stay as plain segments.
function tokenToSegments(token: string, ctx: GroupCtx): string[] {
  // `refLink` tokens carry a TRAILING TARGET, not another path segment:
  //     tasks_NzMxOT$leadRefference%ootbLeads_for_CRUD
  // The path is `tasks_NzMxOT.leadRefference`; `%ootbLeads_for_CRUD` records which entity the link points
  // at. Left in place it becomes the LAST segment, and `matchLeaf` buckets candidates by exactly that — so
  // the lookup misses before any suffix test runs and the token lands as an orphan. Stripping it makes the
  // segments identical to the reference field's own skeleton leaf, which is the whole point: this is the
  // same field, addressed for a different job. No other lane emits this suffix, so the test is unambiguous.
  token = token.replace(/%[^%$]+_for_CRUD$/, '');
  // A field reached through a row's reference is written `…%rows$$ref.field`: each dot is a hop, as `$` is.
  if (token.startsWith('entity_reference_for_AOB$')) token = token.replace(/\$\$([^$]+)$/, (_m, path: string) => '$$' + path.split('.').join('$'));
  const flat = token.split('$data$').join('').split('$$').join('').split('%').join('').split('$').join('');
  const raw = flat.split('').filter(Boolean);
  const out: string[] = [];
  for (let i = 0; i < raw.length; i++) {
    if ((raw[i] === 'local_variable' || raw[i] === 'global_variable') && i + 1 < raw.length) {
      const v = raw[i + 1];
      const val = ctx.localVars[v] || ctx.globalVars[v];
      const eid = val && (val.entityId || val.entityName);
      if (eid) { out.push(eid); i++; continue; }
    }
    out.push(raw[i]);
  }
  return out;
}
const isSegSuffix = (skel: string[], tok: string[]): boolean => skel.length <= tok.length && skel.every((s, i) => s === tok[tok.length - skel.length + i]);
// entity-field entries go through the skeleton; standalone / pageVar are handled directly (reliable parse, no deep path)
function isEntityFieldEntry(token: string, scope: string): boolean {
  // `refLink` joins these (2026-07-30): a `…_for_CRUD` token addresses a REAL ENTITY_REFERENCE FIELD of a
  // real entity, so it belongs on that field's node — not in a standalone group. Without this it takes the
  // standalone path, where `parseToken` has no case for it.
  if (scope === 'newEntry' || scope === 'AOB_reference' || scope === 'section' || scope === 'refLink') return true;
  if ((scope === 'local' || scope === 'global') && (token.startsWith('local_variable%') || token.startsWith('global_variable%'))) return true;
  return false;
}

/**
 * Assemble the `entries` for buildGroupedView from BOTH halves of the atlas map.
 *
 * The map is split across THREE namespaces (atlas-map.service.ts):
 *   page     → `page.atlasVariableMap`       local, newEntry, AOB_reference, section, entityName, AAOB
 *   global   → `globalStore.atlasMeta.map`   global
 *   navigate → `globalStore.atlasMeta.navMap` NAVIGATE  (split from global 2026-08-19)
 *
 * Passing only the page half silently drops every global variable and every navigation
 * target — the grouped view comes back with `globalVars: []` and looks correct. Measured
 * 2026-07-27 across 19 benchmark pages: 378 local tokens, 0 global, while the functions on
 * those pages plainly use `global.BudgetEnity` and `global.editOotbLeadsEntity`.
 *
 * Use this rather than building `entries` by hand.
 */
export function collectMapEntries(
  pageMap: Record<string, { id?: string; type?: string; scope?: string; deleted?: boolean }> | null | undefined,
  globalMap: Record<string, { id?: string; type?: string; scope?: string; deleted?: boolean }> | null | undefined,
  page: Page | null | undefined,
  navMap?: Record<string, { id?: string; type?: string; scope?: string; deleted?: boolean }> | null | undefined,
): GroupInput[] {
  // Usage comes from the page as it stands. The `usages` saved on map entries went stale whenever the page
  // changed after a save, and a global's saved record held other pages' uses that this page cannot vouch for.
  const usage = page ? usageOnMaps(page, pageMap, globalMap, navMap) : new Map<string, VariableUsage>();
  const out: GroupInput[] = [];
  const seen = new Set<string>();
  for (const src of [pageMap, globalMap, navMap]) {
    for (const [token, v] of Object.entries(src || {})) {
      if (seen.has(token)) continue;                 // page half wins on the (unexpected) overlap
      seen.add(token);
      // A tombstone keeps a gone variable's id from being reused; it is not a handle. Spotlight never offered one.
      if (v?.deleted) continue;
      out.push({ token, id: v?.id || token, type: v?.type || '', scope: v?.scope || 'local', usage: usage.get(token) ?? null });
    }
  }
  return out;
}

// Was (until 2026-10-22): the saved `usages` were read off each entry (`normalizeUsage`).

export function buildGroupedView(entries: GroupInput[], ctx: GroupCtx): GroupedView {
  const hide = ctx.agent ? HIDDEN_FOR[ctx.agent] : undefined;

  // 1. skeleton roots = page.entites (authoritative import gate); fall back to deriving from the entries
  let rootIds = ctx.pageEntites && ctx.pageEntites.length ? [...new Set(ctx.pageEntites)] : [];
  if (!rootIds.length) {
    const s = new Set<string>();
    for (const e of entries) { if (!isEntityFieldEntry(e.token, e.scope)) continue; const p = parseToken(e.token, e.scope); if (p.kind === 'entityField' && p.root) s.add(resolveCanonical(p.root, ctx).id); }
    rootIds = [...s];
  }
  // 1b. UNIMPORTED GLOBAL ENTITY VARS (DECIDED 2026-07-27) ──────────────────────────────────────────────
  // The import gate stays IRON-CLAD: skeleton roots are `page.entites` and nothing else (widening roots to
  // var-held entities was measured at 2.6x skeleton bloat and rejected — see the grouped-map decision record).
  //
  // A global variable is page-independent, so it can hold an entity THIS page never imported. Example:
  // `global.editOotbMetaLeadFormIngestionConfigEdit` holds `ootbMetaLeadFormIngestionConfig`, which
  // leadManagementPage does not import. Its 25 minted field tokens ($6…$30) have no skeleton to match.
  // Rule: that variable is NOT available on this page — drop the variable AND its field tokens.
  //
  // NOT silently. A dropped variable that leaves no trace is indistinguishable from a bug (it is precisely
  // what made "the globals are missing from the map" look true). Every drop is counted in
  // `stats.droppedUnimported` and the variable names are listed in `stats.unimportedVars`, so a caller can
  // tell "this page has no such variable" apart from "the builder lost it". The remedy is a DATA fix —
  // import the entity on the page — not a code bypass.
  const rootSet = new Set(rootIds);
  const unimportedVars = new Set<string>();
  for (const e of entries) {
    if (e.scope !== 'global' || !e.token.startsWith('global_variable%')) continue;
    const p = parseToken(e.token, e.scope);
    if (p.kind !== 'entityField' || !p.root) continue;
    if (!rootSet.has(resolveCanonical(p.root, ctx).id)) unimportedVars.add(p.root);
  }
  // The standalone `globalVariable$<name>` entry for the same variable goes too — the record is unreachable here.
  const isUnimported = (token: string, scope: string): boolean => {
    if (scope === 'global' && token.startsWith('global_variable%')) { const p = parseToken(token, scope); return !!p.root && unimportedVars.has(p.root); }
    if (scope === 'global' && token.startsWith('globalVariable$')) { let n = token.slice(15); if (n.endsWith('.data')) n = n.slice(0, -5); return unimportedVars.has(n); }
    return false;
  };

  // 2. build the skeleton; index leaves by last segment for O(1) suffix match; capture entity name/description
  const leafByLast = new Map<string, SkelLeaf[]>();
  const entityName = new Map<string, string>(), entityDesc = new Map<string, string>();
  // full path → that node's schema description. Intermediate hops (an AOB handle, a followed reference)
  // are real fields with real descriptions; without this they would render as bare segment names.
  const hopDesc = new Map<string, string>();
  // full path → the entity a REFERENCE hop points at. Same capture as hopDesc; see GroupedField.refEntity.
  const hopRef = new Map<string, string>();
  // full path → that node's composed query-column handle. Same capture again, because an INTERMEDIATE
  // node is itself a filterable column: `WHERE leadRefference _eq <a lead>` targets the hop, not
  // something inside it (5 of 33 corpus query sites). AAOB nodes are deliberately absent — stamped for
  // identity, never offered as columns.
  const hopColumnId = new Map<string, string>();
  // Every leaf, kept whole — step 3b walks these to place columns no TOKEN reaches (see there).
  const allLeaves: SkelLeaf[] = [];
  for (const eid of rootIds) {
    const ent = ctx.getEntityById(eid);
    if (ent) { entityName.set(eid, ent.name || eid); if (ent.description) entityDesc.set(eid, String(ent.description).trim()); }
    for (const lf of buildSkeleton(eid, ctx.getEntityById)) {
      allLeaves.push(lf);
      const last = lf.segs[lf.segs.length - 1];
      (leafByLast.get(last) || leafByLast.set(last, []).get(last)!).push(lf);
      if (lf.desc) hopDesc.set(lf.segs.join('\u0000'), lf.desc);
      if (lf.refEntity) hopRef.set(lf.segs.join('\u0000'), lf.refEntity);
      if (lf.columnId && !lf.inAaob) hopColumnId.set(lf.segs.join('\u0000'), lf.columnId);
    }
  }
  const matchLeaf = (segs: string[]): SkelLeaf | null => { const cands = leafByLast.get(segs[segs.length - 1]); if (!cands) return null; for (const lf of cands) if (isSegSuffix(lf.segs, segs)) return lf; return null; };

  // 2b. entityName lane → the handle for each entity (2026-07-27). The entityName token IS the entity id,
  // so this is a direct id→handle index. It moves onto the entity node, and the "Entity names" standalone
  // group is dropped: an entity that carries its own handle needs no separate lookup list.
  const entityIdByKey = new Map<string, string>();
  for (const e of entries) if (e.scope === 'entityName') entityIdByKey.set(e.token, e.id);

  // 3. place every entry
  const roots = new Map<string, MutEntity>();
  /**
   * THE ENTITY HANDLE — `$E_<n>` from the entity doc, NOT the page-minted `entityName`-lane id (2026-07-29).
   *
   * Both existed and were redundant. `$AC242` is re-minted per page, so the same entity had a different
   * handle on every page it appears on; `Entity.atlasEntityId` is stamped once and pinned for life, and
   * it is what `of` / `holdsEntity` / `queryColumns[].refEntity` should all join on. A column handle is
   * `$Q<e>_<n>` and already names its entity by that same number, so using the page id here would have
   * meant two unrelated numbering schemes describing one entity in a single payload.
   *
   * The `entityName`-lane id remains the FALLBACK for an entity the P2 backfill did not reach — prod
   * `MjU0OD`, or a client added since. Mixed handles in one payload are still resolvable (every id maps
   * through `entities[].token`), so this degrades rather than breaks; the fallback disappears when the
   * remaining clients are stamped.
   */
  const ensureRoot = (id: string) => { let r = roots.get(id); if (!r) { r = { key: id, root: true, id: ctx.getEntityById(id)?.atlasEntityId || entityIdByKey.get(id), name: entityName.get(id) || id, desc: entityDesc.get(id), status: ctx.getEntityById(id)?.status, fields: new Map() }; roots.set(id, r); } return r; };
  const localVars: GroupedVariant[] = [], globalVars: GroupedVariant[] = [];
  const standaloneMap = new Map<string, string[]>();
  const standaloneIds = new Map<string, StandaloneItem[]>();   // same groups, id-carrying (2026-07-27)
  let matched = 0, unified = 0, orphans = 0, droppedUnimported = 0, droppedLane = 0, heldByUnplaced = 0;
  const droppedLanes = new Set<string>();

  for (const e of entries) {
    // Lane routing. COUNTED (2026-07-27): this used to `continue` with no trace, so a hidden lane vanished
    // silently — the agent simply could not see that capability and nothing said so. It read as clean on the
    // 18 benchmark pages only because none of them carry section tokens (89 exist elsewhere in the corpus).
    if (hide && hide.has(e.scope)) { droppedLane++; droppedLanes.add(e.scope); continue; }
    if (isUnimported(e.token, e.scope)) { droppedUnimported++; continue; }  // §1b — counted, never silent
    // standalone + pageVar: reliable parse, no deep path
    if (!isEntityFieldEntry(e.token, e.scope)) {
      const p = parseToken(e.token, e.scope);
      if (p.kind === 'standalone') {
        // entityName tokens now live ON the entity node (id/token) — no separate group. Still counted.
        if (e.scope === 'entityName') { matched++; continue; }
        (standaloneMap.get(p.group!) || standaloneMap.set(p.group!, []).get(p.group!)!).push(p.name!);
        // A NAVIGATE token IS a page id, so its description is looked up by that id. Other standalone
        // lanes (entity names, AAOB fields) are described elsewhere and pass through undescribed here.
        (standaloneIds.get(p.group!) || standaloneIds.set(p.group!, []).get(p.group!)!).push({
          id: e.id, token: e.token, type: e.type, scope: e.scope, usage: usageOf(e.usage),
          ...(e.scope === 'NAVIGATE' && ctx.pageDescriptions?.[e.token] ? { description: ctx.pageDescriptions[e.token] } : {}),
        });
        matched++;
      }
      else if (p.kind === 'pageVar') {
        const lane = p.isList ? (p.space === 'local' ? 'local list' : 'global list') : (p.space === 'local' ? 'local var' : 'global var');
        // A list or entity variable HOLDS records of an entity — it is not a page scalar. Hang it off
        // that entity as a holder (2026-07-27) so `SET_ENTITY_FROM_LIST` / `QUERY` have an addressable
        // operand in the place the records live. localVars/globalVars are left as SCALARS only.
        const holds = p.isList || /ENTITY_VARIABLE/.test(e.type);
        if (holds && p.name) {
          const canon = resolveCanonical(p.name, ctx);
          if (canon.id && canon.id !== p.name && rootSet.has(canon.id)) {
            const root = ensureRoot(canon.id);
            (root.heldBy ||= []).push({
              id: e.id, token: e.token, name: p.name,
              kind: p.isList ? 'list' : 'entity', type: e.type, scope: e.scope,
              meaning: ctx.varDescriptions?.[p.name],
              usage: usageOf(e.usage),
            });
            matched++;
            continue;
          }
          heldByUnplaced++;   // entity not imported on this page — keep it flat rather than lose it
        }
        (p.space === 'local' ? localVars : globalVars).push({
          id: e.id, token: e.token, type: e.type, lane, scope: e.scope,
          name: p.name,                                        // without this the variant is anonymous
          meaning: p.name ? ctx.varDescriptions?.[p.name] : undefined,
          usage: usageOf(e.usage),
          description: describe({ type: e.type, lane }, e.usage),
        });
        matched++;
      }
      continue;
    }
    // entity-field: SUFFIX-MATCH to the skeleton → authoritative path + field description
    const leaf = matchLeaf(tokenToSegments(e.token, ctx));
    if (!leaf) { orphans++; continue; }                      // stale token (removed field / deleted entity)
    matched++;
    const entityId = leaf.segs[0], rest = leaf.segs.slice(1);
    const field = rest[rest.length - 1], hops = rest.slice(0, -1);
    // Walk the path as FIELDS: each hop is the ARRAY_OF_OBJECT / ENTITY_REFERENCE field it names, and the
    // leaf hangs inside it. `hopDesc` gives each hop its own schema description (captured from the
    // skeleton's handle leaves), so an intermediate field is described, not just a bare segment name.
    let holder: { fields: Map<string, MutField> } = ensureRoot(entityId);
    const walked: string[] = [entityId];
    for (const seg of hops) {
      walked.push(seg);
      const k = walked.join('\u0000');
      holder = ensureField(holder, seg, hopDesc.get(k), hopRef.get(k), hopColumnId.get(k));
    }
    // The leaf takes its handle from the matched skeleton leaf, EXCEPT inside an AAOB — those are stamped
    // for identity but must never surface as a filterable column (aaobField + key is their mechanism).
    // `leaf.refEntity` is passed (fixed 2026-07-30). It used to be `undefined` here, so a reference field
    // learned its target ONLY when some other token happened to pass THROUGH it and set `hopRef`. A
    // reference nothing traverses kept its columnId but lost its target — and `collectColumns` tests
    // `f.refEntity` before emitting a reference column, so that column was silently never offered.
    // …EXCEPT the AAOB container, which keeps its handle (2026-08-04, G2): it is the column
    // `add_condition`/`add_order` name in `field`. Only its interior stays withheld.
    const fn = ensureField(holder, field, leaf.desc || undefined, leaf.refEntity,
      leaf.inAaob && !leaf.aaobRoot ? undefined : leaf.columnId);
    if (leaf.aaobRoot) fn.aaob = true;
    // A refLink entry is NOT a variant. A variant is a ROUTE TO READ this field's value; this is the same
    // field addressed for WRITING a link, so it lands as a second handle on the node rather than as another
    // way to read it. Folding it into `variants` would offer it wherever a value is expected — and it
    // resolves to an ENTITYREFERENCEFORCRUD variable, which is not a value at all.
    // NO `matched++` HERE — line 735 already counted this entry (fixed 2026-08-03). Counting again broke
    // the no-silent-drop invariant `entries === parsed + orphans + droppedUnimported + droppedLane` by
    // exactly the refLink count: measured across 17 real pages, |delta| equalled the page's linkId count on
    // 17 of 17. The over-count was on the ACCOUNTED-FOR side, so nothing was ever silently dropped — but the
    // check that would have proved it could not be used, which is the same thing as not having it.
    // Its usage rides on the link: dropping it with the entry lost the functions that write the link.
    // Was (until 2026-10-22, the link carried its uses): if (e.scope === 'refLink') { if (!fn.linkId) { fn.linkId = e.id; fn.linkToken = e.token; } continue; }
    if (e.scope === 'refLink') { if (!fn.linkId) { fn.linkId = e.id; fn.linkToken = e.token; const u = usageOf(e.usage); if (u) fn.linkUsage = u; } continue; }
    const p = parseToken(e.token, e.scope);                  // for the variant's lane label / sectionCtx / via (reliable, shallow)
    const laneLabel = LANE_LABEL[p.lane!] || p.lane || e.scope;
    const via = p.root && p.root !== entityId ? p.root : undefined;   // the source var, when the root was an entity-var
    // `via` names the variable this handle is rooted in, so its backfilled description is available here —
    // the scenario the holder was created for. Undefined on synthesised lanes, where `via` is undefined
    // because the root IS the entity id and no user variable stands behind it (see GroupedVariant.meaning).
    fn.variants.push({ id: e.id, token: e.token, type: e.type, lane: laneLabel, scope: e.scope, via, sectionCtx: p.sectionCtx, meaning: via ? ctx.varDescriptions?.[via] : undefined, usage: usageOf(e.usage), description: describe({ type: e.type, lane: laneLabel, via, sectionCtx: p.sectionCtx }, e.usage) });
    if (fn.variants.length === 2) unified++;                 // count each field the moment it becomes multi-lane
  }

  // 3b. COLUMNS NO TOKEN REACHES (2026-08-05).
  //
  // Everything above is placed by walking ENTRIES, so a field exists in this tree only if some minted
  // Blockly variable reaches it. That is right for variables and wrong for COLUMNS: a column inside an AOB
  // that hangs off a REFERENCE — `ootbLeads.metaFormRef.questions.key` — is stamped, legal to filter on,
  // and offered by the human picker (`enumerateTargetFields` drills references then AOBs), but nothing
  // mints a variable for it, so it never appeared. Measured: 86 such columns across the 17 corpus maps,
  // all on NzMxMD, up to 31 on one page.
  //
  // These nodes carry a `columnId` and NO variants — filterable, not readable — which the pick list
  // already understands (`pickable = variants.length > 0`; a variant-less node renders as a ROUTE, exactly
  // as `metaFormRef` does today). `columnType` carries the type `collectColumns` would otherwise read off
  // a variant.
  //
  // ADDS NO ENTRIES, so the no-silent-drop invariant `entries === parsed + orphans + droppedUnimported +
  // droppedLane` is untouched — these were never entries to begin with.
  for (const lf of allLeaves) {
    if (lf.kind !== 'scalar' || !lf.columnId || lf.inAaob) continue;
    const type = SKEL_SCALAR_TYPE[lf.type];
    if (!type) continue;                                     // not a filterable scalar
    const [entityId, ...rest] = lf.segs;
    if (!roots.has(entityId)) continue;                      // not an imported root — the gate stays iron-clad
    const field = rest[rest.length - 1];
    const hops = rest.slice(0, -1);
    let holder: { fields: Map<string, MutField> } = ensureRoot(entityId);
    const walked: string[] = [entityId];
    for (const seg of hops) {
      walked.push(seg);
      const k = walked.join('\u0000');
      holder = ensureField(holder, seg, hopDesc.get(k), hopRef.get(k), hopColumnId.get(k));
    }
    const fn = ensureField(holder, field, lf.desc || undefined, undefined, lf.columnId);
    if (!fn.columnType) fn.columnType = type;
  }

  return {
    entities: [...roots.values()].map(freeze).sort((a, b) => a.name.localeCompare(b.name)),
    localVars, globalVars,
    standalone: [...standaloneMap.entries()].map(([group, names]) => ({ group, names, items: standaloneIds.get(group) || [] })),
    stats: { entries: entries.length, parsed: matched, unified, orphans, droppedUnimported, unimportedVars: [...unimportedVars], droppedLane, droppedLanes: [...droppedLanes], heldByUnplaced },
  };
}

/* ── SUPERSEDED 2026-07-26 — the PARSE-BASED grouped build (kept per comment-don't-delete). Replaced by the
 *    forward-skeleton + segment-suffix build above (§12): token inversion here was lossy on edge cases. ──
export function buildGroupedView_parseBased(entries: GroupInput[], ctx: GroupCtx): GroupedView {
  const hide = ctx.agent ? HIDDEN_FOR[ctx.agent] : undefined;
  const roots = new Map<string, MutEntity>();
  const ensureRoot = (id: string, name: string) => { let r = roots.get(id); if (!r) { r = { key: id, name, fields: new Map(), children: new Map() }; roots.set(id, r); } return r; };
  const localVars: GroupedVariant[] = [], globalVars: GroupedVariant[] = [];
  const standaloneMap = new Map<string, string[]>();
  let parsed = 0, unified = 0;

  for (const e of entries) {
    if (hide && hide.has(e.scope)) continue;                 // lane routing
    const p = parseToken(e.token, e.scope);
    if (p.kind === 'unparsed') continue;
    parsed++;
    if (p.kind === 'standalone') { (standaloneMap.get(p.group!) || standaloneMap.set(p.group!, []).get(p.group!)!).push(p.name!); continue; }
    if (p.kind === 'pageVar') {
      const lane = p.isList ? (p.space === 'local' ? 'local list' : 'global list') : (p.space === 'local' ? 'local var' : 'global var');
      const variant: GroupedVariant = { id: e.id, type: e.type, lane, scope: e.scope, description: describe({ type: e.type, lane }, e.usage) };
      (p.space === 'local' ? localVars : globalVars).push(variant);
      continue;
    }
    const canon = resolveCanonical(p.root!, ctx);
    let node = ensureRoot(canon.id, canon.name);
    for (const seg of p.nesting!) node = ensureChild(node, seg);
    const fk = p.field || '(the list itself)';
    let fn = node.fields.get(fk);
    if (!fn) { fn = { field: fk, variants: [] }; node.fields.set(fk, fn); }
    const laneLabel = LANE_LABEL[p.lane!] || p.lane!;
    const via = canon.id !== p.root ? p.root : undefined;
    fn.variants.push({ id: e.id, token: e.token, type: e.type, lane: laneLabel, scope: e.scope, via, sectionCtx: p.sectionCtx, usage: usageOf(e.usage), description: describe({ type: e.type, lane: laneLabel, via, sectionCtx: p.sectionCtx }, e.usage) });
    if (fn.variants.length === 2) unified++;
  }

  return {
    entities: [...roots.values()].map(freeze).sort((a, b) => a.name.localeCompare(b.name)),
    localVars, globalVars,
    standalone: [...standaloneMap.entries()].map(([group, names]) => ({ group, names, items: [] })),  // superseded builder: names only
    stats: { entries: entries.length, parsed, unified, orphans: 0 },
  };
}
*/
